import { Badge, Text } from '@mantine/core'
import { GenericTable, TableColumn } from './Table/GenericTable'
import { useGenericTable } from './Table/useGenericTable'
import { useCurrentShifts, CurrentShift } from '@/hooks/useCurrentShifts'
import { formatDateTime } from '@/utils/datetime'
import { useTranslation } from 'react-i18next'

export function CurrentShiftsTable() {
  const { t } = useTranslation('shifts')
  const { data: shifts = [], isLoading, error } = useCurrentShifts()

  const { currentPage, tableColumns, paginatedData, handlePageChange, setTableColumns } = useGenericTable({
    defaultSortField: 'start',
    defaultSortDirection: 'asc',
    tableKey: 'currentShiftsTable',
    columns: [
      {
        key: 'vehicle_id',
        label: t('vehicle'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => <Text fw={500}>{shift.vehicle_id}</Text>,
      },
      {
        key: 'driver',
        label: t('driver'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => <Text>{shift.driver || 'N/A'}</Text>,
      },
      {
        key: 'start',
        label: t('startTime'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => <Text>{formatDateTime(shift.start)}</Text>,
      },
      {
        key: 'end',
        label: t('endTime'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => <Text>{formatDateTime(shift.end)}</Text>,
      },
      {
        key: 'status',
        label: t('status'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => {
          const statusConfig = {
            running: { color: 'green', label: t('running') },
            upcoming: { color: 'blue', label: t('upcoming') },
            completed: { color: 'gray', label: t('completed') },
          }
          const config = statusConfig[shift.status]
          return (
            <Badge variant="light" color={config.color}>
              {config.label}
            </Badge>
          )
        },
      },
    ] as TableColumn<CurrentShift>[],
    filteredData: shifts,
  })

  return (
    <GenericTable
      data={shifts}
      columns={tableColumns}
      onColumnsChange={setTableColumns}
      isLoading={isLoading}
      error={error}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      hideConfig={true}
      getRowKey={shift => shift.id.toString()}
      emptyMessage={t('noCurrentOrUpcomingShiftsFound')}
      paginatedData={paginatedData}
      itemsPerPage={Infinity}
      hideCsvDownload={true}
    />
  )
}
